import React, { useEffect, useRef, useState, useCallback } from "react";
import * as d3 from "d3";

interface Station {
  _id: string;
  name: string;
  latLng: [number, number];
  displayName?: string;
}

interface Props {
  markers: Station[];
  onMarkerSelection: (index: string) => void;
}

const COLORS = {
  ocean: "#f0f7ff",
  land: "#e2e8f0",
  landStroke: "#cbd5e1",
  markerFill: "#0ea5e9",
  markerStroke: "#fff",
  markerPulse: "#0ea5e9",
  labelFill: "#334155",
  graticule: "#e2e8f0",
};

const VB_W = 960;
const VB_H = 500;

function Map({ markers, onMarkerSelection }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const initialized = useRef(false);
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const markersRef = useRef(markers);
  const onSelectRef = useRef(onMarkerSelection);

  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    text: string;
    x: number;
    y: number;
  }>({ visible: false, text: "", x: 0, y: 0 });

  // Keep refs in sync so D3 callbacks use latest values
  markersRef.current = markers;
  onSelectRef.current = onMarkerSelection;

  const showTooltip = useCallback((evt: MouseEvent, text: string) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const rect = wrapper.getBoundingClientRect();
    setTooltip({
      visible: true,
      text,
      x: evt.clientX - rect.left + 12,
      y: evt.clientY - rect.top - 8,
    });
  }, []);

  const hideTooltip = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  useEffect(() => {
    if (!svgRef.current || initialized.current) return;
    initialized.current = true;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const projection = d3
      .geoNaturalEarth1()
      .fitSize([VB_W - 40, VB_H - 20], { type: "Sphere" } as any)
      .translate([VB_W / 2, VB_H / 2]);

    const path = d3.geoPath().projection(projection);

    const root = svg.append("g");

    // Ocean
    root
      .append("path")
      .datum({ type: "Sphere" } as any)
      .attr("d", path as any)
      .attr("fill", COLORS.ocean)
      .attr("stroke", COLORS.landStroke)
      .attr("stroke-width", 0.5)
      .attr("vector-effect", "non-scaling-stroke");

    // Graticule
    root
      .append("path")
      .datum(d3.geoGraticule10())
      .attr("d", path as any)
      .attr("fill", "none")
      .attr("stroke", COLORS.graticule)
      .attr("stroke-width", 0.3)
      .attr("vector-effect", "non-scaling-stroke");

    const landGroup = root.append("g").attr("class", "land");
    const markerGroup = root.append("g").attr("class", "markers");

    // Zoom
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 60])
      .translateExtent([
        [0, 0],
        [VB_W, VB_H],
      ])
      .on("zoom", (evt) => {
        root.attr("transform", evt.transform);
        applyZoom(markerGroup, evt.transform);
      });
    svg.call(zoom);
    zoomRef.current = zoom;

    d3.json<any>("/world.geojson").then((world) => {
      if (!world) return;
      landGroup
        .selectAll("path")
        .data(world.features)
        .enter()
        .append("path")
        .attr("d", (d: any) => path(d)!)
        .attr("fill", COLORS.land)
        .attr("stroke", COLORS.landStroke)
        .attr("stroke-width", 0.4)
        .attr("vector-effect", "non-scaling-stroke");

      drawMarkers(
        markerGroup,
        projection,
        markersRef.current,
        onSelectRef,
        showTooltip,
        hideTooltip
      );
      applyZoom(markerGroup, d3.zoomTransform(svgRef.current!));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redraw markers when data changes
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const markerGroup = svg.select<SVGGElement>("g.markers");
    if (markerGroup.empty()) return;

    const projection = d3
      .geoNaturalEarth1()
      .fitSize([VB_W - 40, VB_H - 20], { type: "Sphere" } as any)
      .translate([VB_W / 2, VB_H / 2]);

    drawMarkers(
      markerGroup,
      projection,
      markers,
      onSelectRef,
      showTooltip,
      hideTooltip
    );
    applyZoom(markerGroup, d3.zoomTransform(svgRef.current));
  }, [markers, showTooltip, hideTooltip]);

  const zoomBy = (factor: number) => {
    if (!svgRef.current || !zoomRef.current) return;
    d3.select(svgRef.current).transition().duration(300).call(zoomRef.current.scaleBy, factor);
  };
  const resetZoom = () => {
    if (!svgRef.current || !zoomRef.current) return;
    d3.select(svgRef.current).transition().duration(500).call(zoomRef.current.transform, d3.zoomIdentity);
  };
  const btn =
    "h-7 rounded-md bg-brand-300 text-white shadow hover:bg-brand-400 transition";

  return (
    <div ref={wrapperRef} className="relative w-full h-full">
      <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5">
        <button onClick={() => zoomBy(1.6)} aria-label="Zoom in" className={btn + " w-7 text-base font-bold leading-none"}>
          +
        </button>
        <button onClick={() => zoomBy(1 / 1.6)} aria-label="Zoom out" className={btn + " w-7 text-base font-bold leading-none"}>
          −
        </button>
        <button onClick={resetZoom} className={btn + " px-3 text-xs font-semibold uppercase"}>
          Reset Zoom
        </button>
      </div>
      {tooltip.visible && (
        <div
          className="absolute z-50 rounded bg-gray-800 px-2 py-1 text-xs text-white shadow pointer-events-none whitespace-nowrap"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          {tooltip.text}
        </div>
      )}
      <svg
        ref={svgRef}
        className="w-full h-full"
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ background: COLORS.ocean }}
      />
    </div>
  );
}

function drawMarkers(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  projection: d3.GeoProjection,
  markers: Station[],
  onSelectRef: React.MutableRefObject<(index: string) => void>,
  showTooltip: (evt: MouseEvent, text: string) => void,
  hideTooltip: () => void
) {
  g.selectAll("*").remove();

  const defs = g.append("defs");
  const radialGrad = defs
    .append("radialGradient")
    .attr("id", "pulse-grad");
  radialGrad.append("stop").attr("offset", "0%").attr("stop-color", COLORS.markerPulse).attr("stop-opacity", 0.5);
  radialGrad.append("stop").attr("offset", "100%").attr("stop-color", COLORS.markerPulse).attr("stop-opacity", 0);

  markers.forEach((station, i) => {
    if (!station.latLng) return;
    const coords: [number, number] = [station.latLng[1], station.latLng[0]];
    const pt = projection(coords);
    if (!pt) return;

    const mountpoint = station.name.toUpperCase();
    const select = () => onSelectRef.current(String(i));

    // Pulse ring
    g.append("circle")
      .attr("class", "station-ring")
      .attr("data-x", pt[0])
      .attr("data-y", pt[1])
      .attr("cx", pt[0])
      .attr("cy", pt[1])
      .attr("r", 9)
      .attr("fill", "url(#pulse-grad)")
      .attr("stroke", COLORS.markerPulse)
      .attr("stroke-width", 1.5)
      .attr("opacity", 0.6)
      .style("animation", "home-pulse 2s ease-out infinite");

    // Dot
    g.append("circle")
      .attr("class", "station-dot")
      .attr("data-x", pt[0])
      .attr("data-y", pt[1])
      .attr("cx", pt[0])
      .attr("cy", pt[1])
      .attr("r", 5)
      .attr("fill", COLORS.markerFill)
      .attr("stroke", COLORS.markerStroke)
      .attr("stroke-width", 1.5)
      .style("cursor", "pointer")
      .on("click", select)
      .on("mouseenter", (event: MouseEvent) => showTooltip(event, mountpoint))
      .on("mouseleave", hideTooltip);

    // Label (clickable)
    g.append("text")
      .attr("class", "station-label")
      .attr("data-x", pt[0])
      .attr("data-y", pt[1])
      .attr("x", pt[0])
      .attr("y", pt[1])
      .attr("dx", 8)
      .attr("dy", 3)
      .style("font-size", "10px") // inline style: the .station-label CSS rule would override an attribute
      .attr("stroke", "#fff")
      .attr("stroke-width", 2.5)
      .attr("paint-order", "stroke")
      .attr("font-weight", "600")
      .attr("fill", COLORS.labelFill)
      .style("cursor", "pointer")
      .text(station.displayName || station.name)
      .on("click", select)
      .on("mouseenter", (event: MouseEvent) => showTooltip(event, mountpoint))
      .on("mouseleave", hideTooltip);
  });
}

// On-screen sizes (px), constant at any zoom
const DOT_R = 5;
const RING_R = 9;
const LABEL_PX = 10;
const LABEL_DX = 8;
const LABEL_DY = 3;

/**
 * Keep markers and labels the same size on screen at any zoom (counter-scale by 1/k), then
 * declutter: a label is shown only if its on-screen box does not overlap an already placed
 * label or another station's dot. Zooming in separates the stations, so more labels appear;
 * hidden names are still available in the hover tooltip.
 */
function applyZoom(
  g: d3.Selection<SVGGElement, unknown, any, any>,
  t: d3.ZoomTransform
) {
  const k = t.k;
  g.selectAll<SVGCircleElement, unknown>(".station-dot")
    .attr("r", DOT_R / k)
    .attr("stroke-width", 1.5 / k);
  g.selectAll<SVGCircleElement, unknown>(".station-ring")
    .attr("r", RING_R / k)
    .attr("stroke-width", 1.5 / k);
  const labels = g
    .selectAll<SVGTextElement, unknown>(".station-label")
    .style("font-size", `${LABEL_PX / k}px`)
    .attr("dx", LABEL_DX / k)
    .attr("dy", LABEL_DY / k)
    .attr("stroke-width", 2.5 / k);

  type Box = { x0: number; y0: number; x1: number; y1: number };
  const overlaps = (a: Box, b: Box) => a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;
  const screen = (el: Element) => [
    t.applyX(parseFloat(el.getAttribute("data-x") || "0")),
    t.applyY(parseFloat(el.getAttribute("data-y") || "0")),
  ];

  // Obstacles: every station dot (with a small margin)
  const dots: Box[] = [];
  g.selectAll<SVGCircleElement, unknown>(".station-dot").each(function () {
    const [x, y] = screen(this);
    dots.push({ x0: x - DOT_R - 1, y0: y - DOT_R - 1, x1: x + DOT_R + 1, y1: y + DOT_R + 1 });
  });

  const placed: Box[] = [];
  labels.each(function () {
    const [x, y] = screen(this);
    // On-screen width is constant (font is counter-scaled): measure once and cache it.
    // A text with display:none measures 0, so make it visible before the first measure.
    let w = parseFloat(this.getAttribute("data-w") || "NaN");
    if (isNaN(w)) {
      this.removeAttribute("display");
      let measured = 0;
      try {
        measured = this.getComputedTextLength() * k; // user units -> viewBox units on screen
      } catch (e) {
        measured = 0;
      }
      if (measured > 0) {
        w = measured;
        this.setAttribute("data-w", String(measured));
      } else {
        w = (this.textContent || "").length * LABEL_PX * 0.6; // fallback estimate, not cached
      }
    }
    const box: Box = {
      x0: x + LABEL_DX - 2,
      y0: y + LABEL_DY - LABEL_PX,
      x1: x + LABEL_DX + w + 2,
      y1: y + LABEL_DY + 3,
    };
    const ownDot = (d: Box) => Math.abs((d.x0 + d.x1) / 2 - x) < 0.5 && Math.abs((d.y0 + d.y1) / 2 - y) < 0.5;
    const hit =
      placed.some((p) => overlaps(box, p)) ||
      dots.some((d) => !ownDot(d) && overlaps(box, d));
    d3.select(this).attr("display", hit ? "none" : null);
    if (!hit) placed.push(box);
  });
}

export default Map;
